import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import {
  Search,
  DollarSign,
  ArrowDownToLine,
  ArrowUpFromLine,
  Lock,
  Unlock,
  Clock,
  RefreshCw,
  X,
  KeyRound,
  History,
  Wallet as WalletIcon,
  AlertTriangle,
  CheckCircle,
  Printer,
  CreditCard,
  Undo2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import ChipBankTabs from '../components/ChipBankTabs';
import CreditPanel from '../components/CreditPanel';
import { useAuth } from '../contexts/AuthContext';
import {
  printReceipt,
  buildReceiptHTML,
  eposPrintReceipt,
} from '../utils/receiptPrinter';
import { buildChipReprintReceipt, REPRINTABLE_CHIP } from '../components/reprintReceipt';

// ----------------------------------------------------------------
// Cage (Chip Bank) Banker Console
// ----------------------------------------------------------------
// Single-screen UI for cage staff. Open/close shift, look up players,
// post deposits and redemptions. Every wallet-mutating action requires
// the banker to re-enter their cage PIN.
//
// Backend routes used: /admin/cage/*  and  (read) /admin/cage/players/*
// ----------------------------------------------------------------

const fmtCents = (cents) => {
  if (cents == null) return '—';
  const dollars = Number(cents) / 100;
  return dollars.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

const fmtTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString();
};

export default function Cage() {
  const { isAdmin, can } = useAuth();
  const [electronicSettings, setElectronicSettings] = useState(null);
  // Shift system — feature flag fetched for every operator so the
  // dashboard knows whether to render the ShiftBar and gate actions on
  // an open shift.
  const [shiftsSettings, setShiftsSettings] = useState({ enabled: false });
  const [shiftsSaving, setShiftsSaving] = useState(false);
  // Receipt printing — auto-print + copy count drive every operator's
  // dashboard, so these are fetched for all staff (not just admins).
  const [receiptSettings, setReceiptSettings] = useState({
    autoPrint: true,
    copies: 2,
    printerHost: '',
  });
  const [receiptSaving, setReceiptSaving] = useState(false);
  // When set, the post-transaction receipt modal is shown.
  const [receipt, setReceipt] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employeeNumber, setEmployeeNumberState] = useState(
    () => (typeof localStorage !== 'undefined' && localStorage.getItem('cage.employeeNumber')) || ''
  );
  const setEmployeeNumber = (v) => {
    setEmployeeNumberState(v);
    if (typeof localStorage !== 'undefined') {
      if (v) localStorage.setItem('cage.employeeNumber', v);
      else localStorage.removeItem('cage.employeeNumber');
    }
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [walletPanel, setWalletPanel] = useState(null); // { wallet, transactions }
  const [walletLoading, setWalletLoading] = useState(false);

  // Modal state
  const [pinModal, setPinModal] = useState(null);
  // shape: { title, description, action: async (pin) => {}, requireDrawer?, requireAmount?, ... }

  // Redemption confirmation state. After the banker enters a code + PIN, the
  // backend lookup runs and we cache the player info + PIN here so the
  // confirmation step can show the photo and a one-click "Pay Out Chips"
  // button. PIN is held only for the few seconds between lookup and claim.
  const [redeemConfirm, setRedeemConfirm] = useState(null);
  // shape: { redemption, pin } — `redemption` matches the lookup payload

  // Phase 1.8 — player-initiated deposit code (mirror of redeem flow).
  // After PinModal lookup, this modal asks the banker to confirm the
  // player photo + count the cash they're handing over.
  const [depositConfirm, setDepositConfirm] = useState(null);
  // shape: { depositCode, pin }

  // Player-initiated credit code (draw / payment). Same two-step pattern:
  // banker looks up the code via PinModal, then confirms the player photo +
  // enters the actual amount before posting to the credit ledger.
  const [creditConfirm, setCreditConfirm] = useState(null);
  // shape: { creditCode, pin, employeeNumber }

  // ----- bootstrap: fetch active session -----
  const fetchActiveSession = useCallback(async () => {
    try {
      const res = await api.get('/admin/cage/sessions/active');
      setActiveSession(res.data.session);
    } catch (err) {
      if (err?.response?.status === 404) {
        setActiveSession(null);
      } else {
        toast.error('Failed to load active session');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActiveSession();
  }, [fetchActiveSession]);

  // Phase 1.8 — fetch FabiCash demo settings for admins only.
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const res = await api.get('/admin/cage/electronic-deposit/settings');
        setElectronicSettings(res.data);
      } catch (e) {
        /* silent — admin will see the panel say 'unavailable' */
      }
    })();
  }, [isAdmin]);

  const toggleElectronicDeposit = async (next) => {
    try {
      const res = await api.put('/admin/cage/electronic-deposit/settings', {
        enabled: next,
      });
      setElectronicSettings(res.data);
      toast.success(
        `Electronic deposit ${next ? 'enabled' : 'disabled'}`
      );
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update');
    }
  };

  // Receipt-printing settings — fetched for every operator so the
  // dashboard knows whether to auto-print and how many copies to send.
  // If this fetch silently fails, printerHost stays "" and every receipt
  // falls back to the browser print dialog. That looked like "the printer
  // integration isn't working" on the chipbank build until we added the
  // console.warn below — keep it so future debugging starts with a hint.
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/cage/receipt-printing/settings');
        setReceiptSettings({
          autoPrint: res.data.autoPrint !== false,
          copies: res.data.copies || 2,
          printerHost: res.data.printerHost || '',
        });
      } catch (e) {
        console.warn(
          '[Cage] Failed to load receipt-printing settings; receipts will fall back to the browser print dialog.',
          e?.response?.status,
          e?.response?.data || e?.message
        );
      }
    })();
  }, []);

  // Shift system flag — fetched for every operator so the dashboard
  // knows whether to render the ShiftBar and require an open shift.
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/cage/shifts/settings');
        setShiftsSettings({ enabled: res.data.enabled === true });
      } catch (e) {
        /* keep client-side default (disabled) */
      }
    })();
  }, []);

  const updateShiftsSettings = async (patch) => {
    setShiftsSaving(true);
    try {
      const res = await api.put('/admin/cage/shifts/settings', patch);
      setShiftsSettings({ enabled: res.data.enabled === true });
      toast.success(
        `Shift system ${res.data.enabled ? 'enabled' : 'disabled'}`
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.error || 'Failed to save shift settings'
      );
    } finally {
      setShiftsSaving(false);
    }
  };

  const updateReceiptSettings = async (patch) => {
    setReceiptSaving(true);
    try {
      const res = await api.put('/admin/cage/receipt-printing/settings', patch);
      setReceiptSettings({
        autoPrint: res.data.autoPrint !== false,
        copies: res.data.copies || 2,
        printerHost: res.data.printerHost || '',
      });
      toast.success('Receipt settings saved');
    } catch (err) {
      toast.error(
        err?.response?.data?.error || 'Failed to save receipt settings'
      );
    } finally {
      setReceiptSaving(false);
    }
  };

  // ----- player search (debounced via button) -----
  const doSearch = async () => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      toast.error('Enter at least 2 characters');
      return;
    }
    setSearching(true);
    try {
      const res = await api.get('/admin/cage/players/search', { params: { q } });
      setSearchResults(res.data.results || []);
      if (!res.data.results?.length) toast('No players found', { icon: '🤷' });
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  // ----- select player → load wallet panel -----
  const selectPlayer = async (player) => {
    setSelectedPlayer(player);
    setWalletPanel(null);
    setWalletLoading(true);
    try {
      const res = await api.get(`/admin/cage/players/${player.id}/wallet`);
      setWalletPanel(res.data);
    } catch (err) {
      toast.error('Failed to load wallet');
    } finally {
      setWalletLoading(false);
    }
  };

  const refreshWallet = async () => {
    if (!selectedPlayer) return;
    setWalletLoading(true);
    try {
      const res = await api.get(`/admin/cage/players/${selectedPlayer.id}/wallet`);
      setWalletPanel(res.data);
    } catch (err) {
      toast.error('Refresh failed');
    } finally {
      setWalletLoading(false);
    }
  };

  // ----- shift open / close -----
  const handleOpenShift = () => {
    setPinModal({
      title: 'Open Shift',
      description: 'Enter your cage PIN to open a new shift.',
      requireDrawer: true,
      submitLabel: 'Open Shift',
      action: async ({ pin, drawerCents, employeeNumber }) => {
        const res = await api.post('/admin/cage/sessions/open', {
          employeeNumber,
          staffPin: pin,
          openingDrawerCents: drawerCents,
        });
        setActiveSession(res.data.session);
        toast.success('Shift opened');
      },
    });
  };

  const handleCloseShift = () => {
    setPinModal({
      title: 'Close Shift',
      description: 'Enter your cage PIN and the closing drawer total. Variance will be flagged for review.',
      requireDrawer: true,
      drawerLabel: 'Closing drawer cents',
      submitLabel: 'Close Shift',
      action: async ({ pin, drawerCents, employeeNumber }) => {
        const res = await api.post(`/admin/cage/sessions/${activeSession.id}/close`, {
          employeeNumber,
          staffPin: pin,
          closingDrawerCents: drawerCents,
        });
        setActiveSession(null);
        const variance = res.data.summary?.varianceCents;
        if (variance && variance !== 0) {
          toast(`Closed with variance ${fmtCents(variance)} — needs review`, { icon: '⚠️' });
        } else {
          toast.success('Shift closed cleanly');
        }
      },
    });
  };

  // ----- deposit -----
  const handleVoidTxn = (txn) => {
    if (!selectedPlayer) return;
    setPinModal({
      title: `Void ${labelForEntryType(txn.entryType).toLowerCase()} \u2014 ${fmtCents(txn.amountCents)}`,
      description:
        'Posts an append-only reversing entry that backs this transaction out of the player\u2019s balance. The original and the reversal both stay on the ledger. Requires a supervisor cage PIN.',
      requireReason: true,
      submitLabel: 'Void transaction',
      action: async ({ pin, employeeNumber, reason }) => {
        await api.post(`/admin/cage/ledger/${txn.id}/void`, {
          reason,
          employeeNumber,
          staffPin: pin,
        });
        toast.success('Transaction voided');
        await refreshWallet();
      },
    });
  };

  const handleDeposit = () => {
    if (shiftsSettings.enabled && !activeSession) return toast.error('Open a shift first');
    if (!selectedPlayer) return;
    setPinModal({
      title: `Deposit chips for ${selectedPlayer.firstName} ${selectedPlayer.lastName}`,
      description: 'Confirm cash received from the player and credit their chip wallet.',
      requireAmount: true,
      submitLabel: 'Post Deposit',
      action: async ({ pin, amountCents, employeeNumber }) => {
        const res = await api.post('/admin/cage/deposits', {
          userId: selectedPlayer.id,
          amountCents,
          cashReceivedCents: amountCents,
          employeeNumber,
          staffPin: pin,
        });
        toast.success(`Deposited ${fmtCents(amountCents)} → new balance ${fmtCents(res.data.wallet.balanceCents)}`);
        setReceipt({
          type: 'deposit',
          reference: res.data.ledgerEntry?.id,
          dateTime: res.data.deposit?.postedAt,
          playerName: `${selectedPlayer.firstName} ${selectedPlayer.lastName}`.trim(),
          memberNumber: selectedPlayer.memberNumber || null,
          amountCents,
          balanceAfterCents:
            res.data.ledgerEntry?.balanceAfterCents ??
            res.data.wallet?.balanceCents,
          cashierEmployeeNumber: employeeNumber,
        });
        await refreshWallet();
      },
    });
  };

  // ----- redeem (two-step: lookup → confirm photo → claim) -----
  // Step 1 lives in the PinModal: code + PIN. On success we close the PIN
  // modal and open a ConfirmRedeemModal that shows the player's photo so
  // the banker can verify the redeemer matches the account holder before
  // chips leave the cage.
  const handleRedeemFromCode = () => {
    if (shiftsSettings.enabled && !activeSession) return toast.error('Open a shift first');
    setPinModal({
      title: 'Look up withdrawal code',
      description:
        'Enter the player\'s withdrawal code from their app, then verify their photo before handing over chips.',
      requireCode: true,
      submitLabel: 'Look Up',
      action: async ({ pin, code, employeeNumber }) => {
        const lookup = await api.post('/admin/cage/redemptions/lookup', {
          code,
          employeeNumber,
          staffPin: pin,
        });
        // Stash the redemption + PIN + employee #; ConfirmRedeemModal
        // renders next and step 2 reuses the same operator credentials.
        setRedeemConfirm({
          redemption: lookup.data.redemption,
          pin,
          employeeNumber,
        });
      },
    });
  };

  const handleConfirmRedeem = async () => {
    if (!redeemConfirm) return;
    const { redemption, pin, employeeNumber: empNum } = redeemConfirm;
    try {
      const claim = await api.post(
        `/admin/cage/redemptions/${redemption.id}/claim`,
        { employeeNumber: empNum, staffPin: pin }
      );
      toast.success(
        `Withdrew ${fmtCents(claim.data.redemption.claimedAmountCents)} for ` +
          (redemption.wallet?.user?.firstName || 'player')
      );
      const redeemUser = redemption.wallet?.user || {};
      setReceipt({
        type: 'redemption',
        reference: claim.data.ledgerEntry?.id || redemption.code,
        dateTime: claim.data.redemption?.claimedAt,
        playerName:
          `${redeemUser.firstName || ''} ${redeemUser.lastName || ''}`.trim() ||
          'Player',
        memberNumber: redeemUser.memberNumber || null,
        amountCents: claim.data.redemption.claimedAmountCents,
        balanceAfterCents:
          claim.data.ledgerEntry?.balanceAfterCents ??
          claim.data.wallet?.balanceCents,
        cashierEmployeeNumber: empNum,
      });
      if (selectedPlayer && redemption.wallet?.user?.id === selectedPlayer.id) {
        await refreshWallet();
      }
      setRedeemConfirm(null);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || 'Withdrawal failed');
    }
  };

  // ----- deposit from code (two-step: lookup → confirm photo + cash → post) -----
  const handleDepositFromCode = () => {
    if (shiftsSettings.enabled && !activeSession) return toast.error('Open a shift first');
    setPinModal({
      title: 'Look up deposit code',
      description:
        "Enter the player's deposit code from their app. Then verify their photo and count the cash before posting.",
      requireCode: true,
      codeLabel: 'Deposit code',
      submitLabel: 'Look Up',
      action: async ({ pin, code, employeeNumber }) => {
        const lookup = await api.post('/admin/cage/deposit-codes/lookup', {
          code,
          employeeNumber,
          staffPin: pin,
        });
        setDepositConfirm({
          depositCode: lookup.data.depositCode,
          pin,
          employeeNumber,
        });
      },
    });
  };

  const handleConfirmDeposit = async (amountCents) => {
    if (!depositConfirm) return;
    const { depositCode, pin, employeeNumber: empNum } = depositConfirm;
    try {
      const res = await api.post(
        `/admin/cage/deposit-codes/${depositCode.id}/post`,
        {
          amountCents,
          cashReceivedCents: amountCents,
          employeeNumber: empNum,
          staffPin: pin,
        }
      );
      toast.success(
        `Loaded ${fmtCents(res.data.deposit.amountCents)} for ` +
          (depositCode.wallet?.user?.firstName || 'player')
      );
      const depositUser = depositCode.wallet?.user || {};
      setReceipt({
        type: 'deposit',
        reference: res.data.ledgerEntry?.id || depositCode.code,
        dateTime: res.data.deposit?.postedAt,
        playerName:
          `${depositUser.firstName || ''} ${depositUser.lastName || ''}`.trim() ||
          'Player',
        memberNumber: depositUser.memberNumber || null,
        amountCents: res.data.deposit.amountCents,
        balanceAfterCents:
          res.data.ledgerEntry?.balanceAfterCents ??
          res.data.wallet?.balanceCents,
        cashierEmployeeNumber: empNum,
      });
      if (selectedPlayer && depositCode.wallet?.user?.id === selectedPlayer.id) {
        await refreshWallet();
      }
      setDepositConfirm(null);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || 'Deposit failed');
    }
  };

  // ----- credit from code (two-step: lookup → confirm photo + amount → post) -----
  // Player-initiated credit codes carry their own kind ('draw'|'payment'),
  // so a single scan action covers both — the confirm modal shows which.
  const handleCreditFromCode = () => {
    if (shiftsSettings.enabled && !activeSession) return toast.error('Open a shift first');
    setPinModal({
      title: 'Look up credit code',
      description:
        "Enter the player's credit code from their app. Then verify their photo and enter the amount before posting.",
      requireCode: true,
      codeLabel: 'Credit code',
      submitLabel: 'Look Up',
      action: async ({ pin, code, employeeNumber }) => {
        const lookup = await api.post('/admin/credit/codes/lookup', {
          code,
          employeeNumber,
          staffPin: pin,
        });
        setCreditConfirm({
          creditCode: lookup.data.creditCode,
          pin,
          employeeNumber,
        });
      },
    });
  };

  const handleConfirmCreditCode = async (amountCents) => {
    if (!creditConfirm) return;
    const { creditCode, pin, employeeNumber: empNum } = creditConfirm;
    try {
      const res = await api.post(
        `/admin/credit/codes/${creditCode.id}/claim`,
        {
          amountCents,
          employeeNumber: empNum,
          staffPin: pin,
        }
      );
      const isDraw = creditCode.kind === 'draw';
      const player = creditCode.user || {};
      toast.success(
        `${isDraw ? 'Drew' : 'Recorded payment of'} ` +
          `${fmtCents(res.data.creditCode.claimedAmountCents)} for ` +
          (player.firstName || 'player')
      );
      setReceipt({
        type: isDraw ? 'credit_draw' : 'credit_payment',
        reference: res.data.ledgerEntry?.id || creditCode.code,
        dateTime: res.data.creditCode?.claimedAt,
        playerName:
          `${player.firstName || ''} ${player.lastName || ''}`.trim() || 'Player',
        memberNumber: player.memberNumber || null,
        amountCents: res.data.creditCode.claimedAmountCents,
        // Credit-receipt lines: remaining balance owed + available credit,
        // taken from the authoritative post-transaction account snapshot.
        balanceOwedCents: res.data.account?.balanceCents,
        availableCreditCents: res.data.account?.availableCents,
        balanceAfterCents: res.data.ledgerEntry?.balanceAfterCents,
        cashierEmployeeNumber: empNum,
      });
      // If the affected player is the one open in the wallet panel, refresh.
      if (selectedPlayer && creditCode.user?.id === selectedPlayer.id) {
        await refreshWallet();
      }
      setCreditConfirm(null);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || 'Credit action failed');
    }
  };

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  if (loading) {
    return (
      <div className="p-8 text-gray-400 flex items-center gap-3">
        <RefreshCw className="animate-spin" size={18} /> Loading cage state...
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <ChipBankTabs />
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
            <WalletIcon className="text-amber-500" size={26} />
            Player Bank — Cage
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Bank cash for chips, withdraw chips back to physical chips, manage shifts.
          </p>
        </div>
      </header>

      {/* Admin panels (Electronic deposit, Receipt printing, Shift system)
          live on the Player Bank > Settings tab — see PlayerBankSettings.jsx. */}

      {/* Shift bar when shifts are on; a slim code-actions bar (Deposit
          / Withdraw from Code) when they're off — so cage staff keep the
          player-code flow regardless of the shift system. */}
      {shiftsSettings.enabled ? (
        <ShiftBar
          activeSession={activeSession}
          employeeNumber={employeeNumber}
          setEmployeeNumber={setEmployeeNumber}
          onOpen={handleOpenShift}
          onClose={handleCloseShift}
          onRedeem={handleRedeemFromCode}
          onDepositCode={handleDepositFromCode}
          onCreditCode={handleCreditFromCode}
        />
      ) : (
        <CodeActionsBar
          employeeNumber={employeeNumber}
          setEmployeeNumber={setEmployeeNumber}
          onRedeem={handleRedeemFromCode}
          onDepositCode={handleDepositFromCode}
          onCreditCode={handleCreditFromCode}
        />
      )}

      {/* Player search */}
      <section className="bg-gray-800 border border-gray-700 rounded-xl p-5">
        <div className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doSearch()}
              placeholder="Search by name, email, phone, or member #"
              className="w-full pl-10 pr-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-amber-500"
            />
          </div>
          <button
            onClick={doSearch}
            disabled={searching}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            {searching ? <RefreshCw className="animate-spin" size={16} /> : <Search size={16} />}
            Search
          </button>
        </div>

        {!!searchResults.length && (
          <div className="mt-3 grid gap-2">
            {searchResults.map((p) => (
              <button
                key={p.id}
                onClick={() => selectPlayer(p)}
                className={`text-left px-4 py-3 rounded-lg border transition-colors ${
                  selectedPlayer?.id === p.id
                    ? 'bg-amber-500/10 border-amber-500'
                    : 'bg-gray-900 border-gray-700 hover:border-gray-600'
                }`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-white font-medium">
                      {p.firstName} {p.lastName}{' '}
                      {p.memberNumber && (
                        <span className="text-gray-500 text-sm font-normal">
                          · #{p.memberNumber}
                        </span>
                      )}
                    </div>
                    <div className="text-gray-400 text-sm">
                      {p.email} · {p.phone}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-amber-400 font-mono text-lg">
                      {fmtCents(p.wallet?.balanceCents ?? 0)}
                    </div>
                    {p.wallet?.status && p.wallet.status !== 'active' && (
                      <span className="text-xs text-red-400 uppercase">
                        {p.wallet.status}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Wallet panel */}
      {selectedPlayer && (
        <WalletPanel
          panel={walletPanel}
          loading={walletLoading}
          onRefresh={refreshWallet}
          onDeposit={handleDeposit}
          onWithdraw={handleRedeemFromCode}
          activeSession={activeSession}
          shiftsEnabled={shiftsSettings.enabled}
          employeeNumber={employeeNumber}
          setEmployeeNumber={setEmployeeNumber}
          memberNumber={selectedPlayer?.memberNumber}
          onReprint={(r) => setReceipt(r)}
          onVoid={handleVoidTxn}
          canVoid={can('wallets:adjust')}
          onClose={() => {
            setSelectedPlayer(null);
            setWalletPanel(null);
          }}
        />
      )}

      {/* PIN modal */}
      {pinModal && (
        <PinModal
          modal={pinModal}
          employeeNumber={employeeNumber}
          setEmployeeNumber={setEmployeeNumber}
          onClose={() => setPinModal(null)}
        />
      )}

      {/* Redemption confirmation (shows player photo before chips leave) */}
      {redeemConfirm && (
        <ConfirmRedeemModal
          redemption={redeemConfirm.redemption}
          onConfirm={handleConfirmRedeem}
          onCancel={() => setRedeemConfirm(null)}
        />
      )}

      {/* Phase 1.8 — deposit-from-code confirmation (photo + cash count) */}
      {depositConfirm && (
        <ConfirmDepositModal
          depositCode={depositConfirm.depositCode}
          onConfirm={handleConfirmDeposit}
          onCancel={() => setDepositConfirm(null)}
        />
      )}

      {/* Player-initiated credit code (draw / payment) confirmation */}
      {creditConfirm && (
        <ConfirmCreditCodeModal
          creditCode={creditConfirm.creditCode}
          onConfirm={handleConfirmCreditCode}
          onCancel={() => setCreditConfirm(null)}
        />
      )}

      {/* Post-transaction receipt — preview + print to the TM-T88VII */}
      {receipt && (
        <ReceiptModal
          receipt={receipt}
          copies={receiptSettings.copies}
          autoPrint={receiptSettings.autoPrint}
          printerHost={receiptSettings.printerHost}
          onClose={() => setReceipt(null)}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------
// Code actions bar — shown when the shift system is disabled
// ----------------------------------------------------------------
// Slim bar with just the employee # input plus Deposit / Withdraw
// from Code. This is the player-generated-code flow that cage staff
// still need without shifts; without this bar those actions would
// disappear with the ShiftBar.
function CodeActionsBar({
  employeeNumber,
  setEmployeeNumber,
  onRedeem,
  onDepositCode,
  onCreditCode,
}) {
  return (
    <section className="bg-gray-800 border border-gray-700 rounded-xl p-5">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="text-sm text-gray-400">
          Process player-generated deposit, withdrawal, and credit codes.
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="text"
            placeholder="Employee #"
            value={employeeNumber}
            onChange={(e) => setEmployeeNumber(e.target.value)}
            className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white w-32 focus:outline-none focus:border-amber-500"
          />
          <button
            onClick={onDepositCode}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg flex items-center gap-2"
          >
            <ArrowDownToLine size={16} /> Deposit from Code
          </button>
          <button
            onClick={onRedeem}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg flex items-center gap-2"
          >
            <ArrowUpFromLine size={16} /> Withdraw from Code
          </button>
          <button
            onClick={onCreditCode}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg flex items-center gap-2"
          >
            <CreditCard size={16} /> Credit Code
          </button>
        </div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------
// Shift status bar
// ----------------------------------------------------------------
function ShiftBar({
  activeSession,
  employeeNumber,
  setEmployeeNumber,
  onOpen,
  onClose,
  onRedeem,
  onDepositCode,
  onCreditCode,
}) {
  return (
    <section className="bg-gray-800 border border-gray-700 rounded-xl p-5">
      <div className="flex flex-wrap items-center gap-4 justify-between">
        <div className="flex items-center gap-4 flex-wrap">
          {activeSession ? (
            <>
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle size={20} />
                <div>
                  <div className="font-medium">Shift open</div>
                  <div className="text-xs text-gray-400">
                    {activeSession.bankerName
                      ? `Opened by ${activeSession.bankerName} · ${fmtTime(activeSession.openedAt)}`
                      : `Opened ${fmtTime(activeSession.openedAt)}`}
                  </div>
                </div>
              </div>
              {activeSession.openingDrawerCents != null && (
                <div className="text-sm text-gray-300">
                  Opening drawer:{' '}
                  <span className="font-mono">
                    {fmtCents(activeSession.openingDrawerCents)}
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2 text-gray-400">
              <Lock size={20} />
              <span>No active shift</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Employee #"
            value={employeeNumber}
            onChange={(e) => setEmployeeNumber(e.target.value)}
            className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white w-32 focus:outline-none focus:border-amber-500"
          />
          {activeSession ? (
            <>
              <button
                onClick={onDepositCode}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg flex items-center gap-2"
              >
                <ArrowDownToLine size={16} /> Deposit from Code
              </button>
              <button
                onClick={onRedeem}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg flex items-center gap-2"
              >
                <ArrowUpFromLine size={16} /> Withdraw from Code
              </button>
              <button
                onClick={onCreditCode}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg flex items-center gap-2"
              >
                <CreditCard size={16} /> Credit Code
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2"
              >
                <Lock size={16} /> Close Shift
              </button>
            </>
          ) : (
            <button
              onClick={onOpen}
              disabled={!employeeNumber}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg disabled:opacity-50 flex items-center gap-2"
            >
              <Unlock size={16} /> Open Shift
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------
// Wallet panel for the selected player
// ----------------------------------------------------------------
function WalletPanel({ panel, loading, onRefresh, onDeposit, onWithdraw, activeSession, shiftsEnabled, onClose, employeeNumber, setEmployeeNumber, memberNumber, onReprint, onVoid, canVoid }) {
  const [view, setView] = useState('bank');
  if (loading) {
    return (
      <section className="bg-gray-800 border border-gray-700 rounded-xl p-6 text-gray-400 flex items-center gap-3">
        <RefreshCw className="animate-spin" size={18} /> Loading wallet...
      </section>
    );
  }
  if (!panel) return null;

  const { wallet, transactions } = panel;
  const items = transactions?.items || [];

  // Voidable within the chip (bank) ledger: deposits + manual adjustments that
  // have not already been reversed. The backend is the authoritative guard
  // (supervisor PIN + single-void); this just decides when to show the button.
  const VOIDABLE_ENTRY_TYPES = new Set(['deposit', 'adjustment_credit', 'adjustment_debit']);
  const reversedEntryIds = new Set(items.filter((i) => i.reversesEntryId).map((i) => i.reversesEntryId));
  const canVoidEntry = (t) =>
    canVoid && VOIDABLE_ENTRY_TYPES.has(t.entryType) && !t.reversesEntryId && !reversedEntryIds.has(t.id);

  return (
    <section className="bg-gray-800 border border-gray-700 rounded-xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">
            {wallet.user?.firstName} {wallet.user?.lastName}
          </h2>
          <div className="text-gray-400 text-sm">
            {wallet.user?.email} · {wallet.user?.phone}
          </div>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-white">
          <X size={20} />
        </button>
      </div>

      {/* Bank / Credit ledger toggle */}
      <div className="flex gap-1 bg-gray-900 border border-gray-700 rounded-lg p-1 w-fit">
        <button
          onClick={() => setView('bank')}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
            view === 'bank' ? 'bg-amber-500 text-black' : 'text-gray-400 hover:text-white'
          }`}
        >
          Bank
        </button>
        <button
          onClick={() => setView('credit')}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
            view === 'credit' ? 'bg-amber-500 text-black' : 'text-gray-400 hover:text-white'
          }`}
        >
          Credit
        </button>
      </div>

      {view === 'credit' && (
        <CreditPanel
          userId={wallet.userId}
          employeeNumber={employeeNumber}
          setEmployeeNumber={setEmployeeNumber}
          activeSession={activeSession}
          shiftsEnabled={shiftsEnabled}
          onReprint={onReprint}
        />
      )}

      {view === 'bank' && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Balance card */}
        <div className="md:col-span-1 bg-gray-900 border border-gray-700 rounded-xl p-5">
          <div className="text-gray-400 text-sm uppercase tracking-wide mb-1">
            Wallet balance
          </div>
          <div className="text-3xl font-mono text-amber-400 mb-2">
            {fmtCents(wallet.balanceCents)}
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span
              className={`px-2 py-0.5 rounded ${
                wallet.status === 'active'
                  ? 'bg-emerald-500/10 text-emerald-400'
                  : wallet.status === 'frozen'
                    ? 'bg-red-500/10 text-red-400'
                    : 'bg-gray-600 text-gray-300'
              }`}
            >
              {wallet.status}
            </span>
            <button onClick={onRefresh} className="text-gray-500 hover:text-amber-400 ml-auto">
              <RefreshCw size={14} />
            </button>
          </div>
          {wallet.status === 'frozen' && wallet.frozenReason && (
            <div className="mt-3 p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-300 flex items-start gap-2">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
              <span>{wallet.frozenReason}</span>
            </div>
          )}

          <div className="mt-5 space-y-2">
            <button
              onClick={onDeposit}
              disabled={(shiftsEnabled && !activeSession) || wallet.status !== 'active'}
              className="w-full px-3 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <ArrowDownToLine size={16} /> Deposit
            </button>
            <button
              onClick={onWithdraw}
              disabled={(shiftsEnabled && !activeSession) || wallet.status !== 'active'}
              title="Look up the player's withdrawal code from their app"
              className="w-full px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <ArrowUpFromLine size={16} /> Withdraw
            </button>
          </div>
        </div>

        {/* Recent transactions */}
        <div className="md:col-span-2 bg-gray-900 border border-gray-700 rounded-xl p-5">
          <div className="flex items-center gap-2 text-gray-400 text-sm uppercase tracking-wide mb-3">
            <History size={14} /> Recent activity
          </div>
          {items.length === 0 ? (
            <div className="text-gray-500 text-sm">No transactions yet.</div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {items.map((t) => (
                <div
                  key={t.id}
                  className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0"
                >
                  <div>
                    <div className="text-white text-sm font-medium">
                      {labelForEntryType(t.entryType)}
                      {t.banker && (
                        <span className="text-gray-500 font-normal">
                          {' '}
                          by {t.banker.name}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">{fmtTime(t.createdAt)}</div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span
                      className={`font-mono ${
                        t.direction === 'credit' ? 'text-emerald-400' : 'text-red-400'
                      }`}
                    >
                      {t.direction === 'credit' ? '+' : '−'}
                      {fmtCents(t.amountCents)}
                    </span>
                    {REPRINTABLE_CHIP.has(t.entryType) && (
                      <button
                        onClick={() =>
                          onReprint?.(
                            buildChipReprintReceipt(t, {
                              playerName:
                                `${wallet.user?.firstName || ''} ${wallet.user?.lastName || ''}`.trim(),
                              memberNumber,
                            })
                          )
                        }
                        className="text-gray-600 hover:text-amber-400"
                        title="Reprint receipt"
                      >
                        <Printer size={14} />
                      </button>
                    )}
                    {canVoidEntry(t) && (
                      <button
                        onClick={() => onVoid?.(t)}
                        className="text-gray-600 hover:text-red-400"
                        title="Void this transaction (posts a reversing entry)"
                      >
                        <Undo2 size={14} />
                      </button>
                    )}
                    {reversedEntryIds.has(t.id) && (
                      <span className="text-[10px] uppercase tracking-wide text-red-400/80 border border-red-500/30 rounded px-1 py-0.5">
                        voided
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}
    </section>
  );
}

function labelForEntryType(type) {
  switch (type) {
    case 'deposit':
      return 'Deposit';
    case 'redeem':
      return 'Withdrawal';
    case 'adjustment_credit':
      return 'Adjustment (credit)';
    case 'adjustment_debit':
      return 'Adjustment (debit)';
    case 'reversal_credit':
    case 'reversal_debit':
      return 'Reversal';
    default:
      return type;
  }
}

// ----------------------------------------------------------------
// PIN modal — used for every wallet-mutating action
// ----------------------------------------------------------------
function PinModal({ modal, employeeNumber, setEmployeeNumber, onClose }) {
  const [pin, setPin] = useState('');
  const [drawerDollars, setDrawerDollars] = useState('');
  const [amountDollars, setAmountDollars] = useState('');
  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const pinInputRef = useRef(null);

  const submit = async () => {
    if (!employeeNumber) {
      toast.error('Enter your cage employee number');
      return;
    }
    if (!pin) {
      toast.error('PIN required');
      return;
    }

    let drawerCents = null;
    let amountCents = null;
    let trimmedCode = null;

    if (modal.requireDrawer && drawerDollars) {
      const n = Number(drawerDollars);
      if (!Number.isFinite(n) || n < 0) return toast.error('Drawer amount must be ≥ 0');
      drawerCents = Math.round(n * 100);
    }
    if (modal.requireAmount) {
      const n = Number(amountDollars);
      if (!Number.isFinite(n) || n <= 0) return toast.error('Amount must be > 0');
      amountCents = Math.round(n * 100);
    }
    if (modal.requireCode) {
      trimmedCode = code.trim().toUpperCase();
      if (trimmedCode.length < 6) return toast.error('Enter a valid code');
    }
    if (modal.requireReason && !reason.trim()) {
      return toast.error('Reason required');
    }

    setSubmitting(true);
    try {
      // Pass employeeNumber explicitly. The action callbacks are created
      // when the modal opens and would otherwise capture a stale value
      // if the operator types their employee # into this modal.
      await modal.action({
        pin,
        drawerCents,
        amountCents,
        code: trimmedCode,
        reason: reason.trim(),
        employeeNumber,
      });
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">{modal.title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        {modal.description && (
          <p className="text-gray-400 text-sm">{modal.description}</p>
        )}

        {/* Always shown + editable — supports multiple bankers sharing one
            workstation. Pre-filled with the last-used number as a
            convenience; each operator confirms or replaces it. */}
        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
            Employee #
          </label>
          <input
            type="text"
            value={employeeNumber}
            onChange={(e) => setEmployeeNumber(e.target.value)}
            className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
            placeholder="Your cage employee number"
            autoFocus={!employeeNumber}
          />
        </div>

        {modal.requireAmount && (
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
              Amount (USD)
            </label>
            <div className="relative">
              <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="number"
                step="0.01"
                min="0"
                value={amountDollars}
                onChange={(e) => setAmountDollars(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
                placeholder="0.00"
                autoFocus
              />
            </div>
          </div>
        )}

        {modal.requireDrawer && (
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
              {modal.drawerLabel || 'Drawer cash (USD, optional)'}
            </label>
            <div className="relative">
              <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="number"
                step="0.01"
                min="0"
                value={drawerDollars}
                onChange={(e) => setDrawerDollars(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
                placeholder="0.00"
              />
            </div>
          </div>
        )}

        {modal.requireCode && (
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
              {modal.codeLabel || 'Withdrawal code'}
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => {
                // Barcode/QR scanners type the code then send Enter. On
                // Enter: if the PIN is already filled, submit; otherwise
                // jump focus to the PIN field so the banker can finish.
                if (e.key !== 'Enter') return;
                e.preventDefault();
                if (pin) {
                  submit();
                } else if (pinInputRef.current) {
                  pinInputRef.current.focus();
                }
              }}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white font-mono uppercase focus:outline-none focus:border-amber-500"
              placeholder="Scan QR or type — e.g. AB23CD45EF67"
              autoFocus={!!employeeNumber}
              maxLength={16}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
            />
            <div className="mt-1 text-xs text-gray-500">
              Tip: scan the player's QR code with a barcode scanner — it fills
              and {pin ? 'submits' : 'advances to the PIN'} automatically.
            </div>
          </div>
        )}

        {modal.requireReason && (
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
              Reason
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
              placeholder="Why is this being voided? (required)"
            />
          </div>
        )}

        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
            <KeyRound size={12} className="inline mr-1" />
            Cage PIN
          </label>
          <input
            ref={pinInputRef}
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
            placeholder="••••"
            autoFocus={!modal.requireAmount && !modal.requireDrawer && !modal.requireCode}
          />
          {employeeNumber && (
            <div className="mt-1 text-xs text-gray-500">
              for employee #{employeeNumber}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-400 hover:text-white"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            {submitting ? <RefreshCw className="animate-spin" size={16} /> : null}
            {modal.submitLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------
// ConfirmRedeemModal
// ----------------------------------------------------------------
// Phase 1.5 fraud control: after the banker enters a code + PIN, this modal
// shows the account holder's photo and name. The banker visually verifies
// the redeemer matches before clicking "Pay Out Chips" (which fires the
// /claim API). Closing this modal cancels the redemption — the code
// remains pending and unclaimed on the server.
function ConfirmRedeemModal({ redemption, onConfirm, onCancel }) {
  const [submitting, setSubmitting] = useState(false);
  const wallet = redemption?.wallet || {};
  const user = wallet.user || {};
  const requested = redemption?.requestedAmountCents;
  // What we'll pay out: the requested amount if specified, else "redeem all"
  // = the current wallet balance.
  const payOutCents =
    requested != null ? requested : wallet.balanceCents;
  const payOutLabel = requested != null
    ? fmtCents(requested)
    : `${fmtCents(wallet.balanceCents)} (full balance)`;

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setSubmitting(false);
    }
  };

  const initials = ((user.firstName || '?').charAt(0) +
    (user.lastName || '').charAt(0)).toUpperCase();

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-md p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Verify withdrawal</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg px-3 py-2 text-amber-200 text-sm flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <div>
            Confirm the person at the cage matches the photo below before handing over the chips.
          </div>
        </div>

        <div className="flex flex-col items-center text-center space-y-3">
          {user.profilePhotoUrl ? (
            <img
              src={user.profilePhotoUrl}
              alt={`${user.firstName || ''} ${user.lastName || ''}`.trim()}
              className="w-32 h-32 rounded-full object-cover border-4 border-amber-500/40"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="w-32 h-32 rounded-full bg-gray-700 border-4 border-amber-500/40 flex items-center justify-center">
              <span className="text-3xl font-semibold text-gray-300">{initials}</span>
            </div>
          )}
          <div>
            <div className="text-xl font-semibold text-white">
              {user.firstName} {user.lastName}
            </div>
            {user.email && (
              <div className="text-sm text-gray-400">{user.email}</div>
            )}
          </div>
        </div>

        <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4 space-y-2 text-sm">
          <Row label="Wallet balance" value={fmtCents(wallet.balanceCents)} />
          <Row label="Withdrawal amount" value={payOutLabel} emphasis />
          <Row label="Code" value={<span className="font-mono">{redemption.code}</span>} />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 text-gray-400 hover:text-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting || !payOutCents || payOutCents <= 0}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            {submitting ? <RefreshCw className="animate-spin" size={16} /> : <CheckCircle size={16} />}
            Process Withdrawal
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, emphasis = false }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-400">{label}</span>
      <span className={emphasis ? 'text-amber-400 font-semibold' : 'text-white'}>
        {value}
      </span>
    </div>
  );
}

// ----------------------------------------------------------------
// ElectronicDepositPanel
// ----------------------------------------------------------------
// Admin-only toggle for the FabiCash electronic-deposit demo. When ON,
// the mobile app shows an "Electronic" deposit option alongside the
// "At the Cage" option. While demoMode is true, no real money moves —
// approvals are simulated server-side and the wallet is credited
// instantly with payment_processor='fabicash_demo'.
export function ElectronicDepositPanel({ settings, onToggle }) {
  const enabled = !!settings?.enabled;
  return (
    <section
      className={`rounded-xl border p-4 flex items-center justify-between gap-4 ${
        enabled
          ? 'bg-amber-900/20 border-amber-700/50'
          : 'bg-gray-800 border-gray-700'
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 w-9 h-9 rounded-lg flex items-center justify-center ${
            enabled ? 'bg-amber-500/20' : 'bg-gray-700'
          }`}
        >
          <AlertTriangle
            size={18}
            className={enabled ? 'text-amber-400' : 'text-gray-400'}
          />
        </div>
        <div>
          <div className="text-white font-medium flex items-center gap-2">
            FabiCash electronic deposit (Demo){' '}
            {enabled && (
              <span className="text-xs px-2 py-0.5 rounded bg-amber-500/30 text-amber-200 font-semibold">
                LIVE TO PLAYERS
              </span>
            )}
          </div>
          <div className="text-sm text-gray-400 mt-0.5">
            {enabled
              ? 'Players currently see a Credit / Debit / ACH deposit option in the app. No real money moves — approvals are simulated.'
              : "Off. Players only see the in-person 'At the Cage' deposit option."}
          </div>
        </div>
      </div>
      <button
        onClick={() => onToggle(!enabled)}
        className={`px-4 py-2 rounded-lg font-medium ${
          enabled
            ? 'bg-gray-700 hover:bg-gray-600 text-white'
            : 'bg-amber-500 hover:bg-amber-600 text-black'
        }`}
      >
        {enabled ? 'Turn off' : 'Turn on'}
      </button>
    </section>
  );
}

// ----------------------------------------------------------------
// ConfirmDepositModal
// ----------------------------------------------------------------
// Phase 1.8 — counterpart of ConfirmRedeemModal for the inbound flow.
// Banker scans/types a deposit code; this modal shows the player photo
// + requested amount (optional) + a cash-count input. Banker enters
// what they actually received, hits "Bank Cash", server posts.
function ConfirmDepositModal({ depositCode, onConfirm, onCancel }) {
  const wallet = depositCode?.wallet || {};
  const user = wallet.user || {};
  const requested = depositCode?.requestedAmountCents;
  const initialDollars =
    requested != null ? (requested / 100).toFixed(2) : '';
  const [cashDollars, setCashDollars] = useState(initialDollars);
  const [submitting, setSubmitting] = useState(false);

  const cashCents = (() => {
    const n = Number(cashDollars);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.round(n * 100);
  })();

  const handleConfirm = async () => {
    if (cashCents == null) {
      toast.error('Enter the cash you received');
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(cashCents);
    } finally {
      setSubmitting(false);
    }
  };

  const initials = ((user.firstName || '?').charAt(0) +
    (user.lastName || '').charAt(0)).toUpperCase();

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-md p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Verify and bank cash</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg px-3 py-2 text-amber-200 text-sm flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <div>
            Confirm the person at the cage matches the photo below, then count
            the cash they're handing you. Enter the actual amount received.
          </div>
        </div>

        <div className="flex flex-col items-center text-center space-y-3">
          {user.profilePhotoUrl ? (
            <img
              src={user.profilePhotoUrl}
              alt={`${user.firstName || ''} ${user.lastName || ''}`.trim()}
              className="w-32 h-32 rounded-full object-cover border-4 border-emerald-500/40"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="w-32 h-32 rounded-full bg-gray-700 border-4 border-emerald-500/40 flex items-center justify-center">
              <span className="text-3xl font-semibold text-gray-300">{initials}</span>
            </div>
          )}
          <div>
            <div className="text-xl font-semibold text-white">
              {user.firstName} {user.lastName}
            </div>
            {user.email && (
              <div className="text-sm text-gray-400">{user.email}</div>
            )}
          </div>
        </div>

        <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4 space-y-2 text-sm">
          <Row
            label="Player requested"
            value={requested != null ? fmtCents(requested) : 'Open (no preset)'}
          />
          <Row label="Code" value={<span className="font-mono">{depositCode.code}</span>} />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
            Cash received (USD) *
          </label>
          <div className="relative">
            <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="number"
              step="0.01"
              min="0"
              value={cashDollars}
              onChange={(e) => setCashDollars(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-lg focus:outline-none focus:border-emerald-500"
              placeholder="0.00"
              autoFocus
            />
          </div>
          {requested != null && cashCents != null && cashCents !== requested && (
            <div className="text-xs text-amber-400 mt-1">
              Note: cash count differs from the player's stated amount of {fmtCents(requested)}.
              Posting will use the cash amount.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 text-gray-400 hover:text-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting || cashCents == null}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            {submitting ? <RefreshCw className="animate-spin" size={16} /> : <CheckCircle size={16} />}
            Bank Cash
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------
// ConfirmCreditCodeModal
// ----------------------------------------------------------------
// Player-initiated credit code (draw / payment). Banker scans/types the
// code; this modal shows the player photo + the account's owed/available
// context + an amount input. For a DRAW the amount is hard-blocked above
// available credit (server rejects); for a PAYMENT it can't exceed owed.
function ConfirmCreditCodeModal({ creditCode, onConfirm, onCancel }) {
  const user = creditCode?.user || {};
  const account = creditCode?.account || {};
  const isDraw = creditCode?.kind === 'draw';
  const requested = creditCode?.requestedAmountCents;
  const owed = Number(account.balanceCents || 0);
  const available = Number(account.availableCents || 0);

  const initialDollars = requested != null ? (requested / 100).toFixed(2) : '';
  const [amountDollars, setAmountDollars] = useState(initialDollars);
  const [submitting, setSubmitting] = useState(false);

  const amountCents = (() => {
    const n = Number(amountDollars);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.round(n * 100);
  })();

  const overDraw = isDraw && amountCents != null && amountCents > available;
  const overPay = !isDraw && amountCents != null && amountCents > owed;

  const handleConfirm = async () => {
    if (amountCents == null) {
      toast.error('Enter an amount');
      return;
    }
    if (overDraw) {
      toast.error(`Draw exceeds available credit (${fmtCents(available)})`);
      return;
    }
    if (overPay) {
      toast.error(`Payment exceeds balance owed (${fmtCents(owed)})`);
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(amountCents);
    } finally {
      setSubmitting(false);
    }
  };

  const initials = ((user.firstName || '?').charAt(0) +
    (user.lastName || '').charAt(0)).toUpperCase();

  // Static class strings (Tailwind JIT can't see interpolated class names).
  const avatarBorder = isDraw ? 'border-indigo-500/40' : 'border-emerald-500/40';

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-md p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <CreditCard size={18} className={isDraw ? 'text-indigo-400' : 'text-emerald-400'} />
            {isDraw ? 'Draw credit' : 'Record credit payment'}
          </h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg px-3 py-2 text-amber-200 text-sm flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <div>
            Confirm the person at the cage matches the photo below, then{' '}
            {isDraw
              ? 'enter the amount of credit to draw (chips handed out).'
              : 'count the payment and enter the amount received.'}
          </div>
        </div>

        <div className="flex flex-col items-center text-center space-y-3">
          {user.profilePhotoUrl ? (
            <img
              src={user.profilePhotoUrl}
              alt={`${user.firstName || ''} ${user.lastName || ''}`.trim()}
              className={`w-32 h-32 rounded-full object-cover border-4 ${avatarBorder}`}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className={`w-32 h-32 rounded-full bg-gray-700 border-4 ${avatarBorder} flex items-center justify-center`}>
              <span className="text-3xl font-semibold text-gray-300">{initials}</span>
            </div>
          )}
          <div>
            <div className="text-xl font-semibold text-white">
              {user.firstName} {user.lastName}
              {user.memberNumber && (
                <span className="text-gray-500 text-sm font-normal"> · #{user.memberNumber}</span>
              )}
            </div>
            {user.email && <div className="text-sm text-gray-400">{user.email}</div>}
          </div>
        </div>

        <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4 space-y-2 text-sm">
          <Row label="Balance owed" value={fmtCents(owed)} />
          <Row label="Available credit" value={fmtCents(available)} />
          <Row
            label="Player requested"
            value={requested != null ? fmtCents(requested) : 'Open (no preset)'}
          />
          <Row label="Code" value={<span className="font-mono">{creditCode.code}</span>} />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
            {isDraw ? 'Credit to draw (USD) *' : 'Payment received (USD) *'}
          </label>
          <div className="relative">
            <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="number"
              step="0.01"
              min="0"
              value={amountDollars}
              onChange={(e) => setAmountDollars(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-lg focus:outline-none focus:border-indigo-500"
              placeholder="0.00"
              autoFocus
            />
          </div>
          {overDraw && (
            <div className="text-xs text-red-400 mt-1">
              Exceeds available credit ({fmtCents(available)}). Raise the player's limit first.
            </div>
          )}
          {overPay && (
            <div className="text-xs text-red-400 mt-1">
              Exceeds balance owed ({fmtCents(owed)}). Enter {fmtCents(owed)} or less.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 text-gray-400 hover:text-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting || amountCents == null || overDraw || overPay}
            className={`px-4 py-2 ${
              isDraw ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-emerald-600 hover:bg-emerald-700'
            } text-white font-semibold rounded-lg disabled:opacity-50 flex items-center gap-2`}
          >
            {submitting ? <RefreshCw className="animate-spin" size={16} /> : <CheckCircle size={16} />}
            {isDraw ? 'Draw Credit' : 'Record Payment'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------
// ShiftSystemPanel — admin toggle for the cage shift system
// ----------------------------------------------------------------
// When off, the ShiftBar is hidden and transactions don't require an
// open shift. Per-PIN attribution still applies. Re-enable for
// cash-drawer reconciliation by shift.
export function ShiftSystemPanel({ settings, onChange, saving }) {
  const enabled = !!settings?.enabled;
  return (
    <section className="rounded-xl border border-gray-700 bg-gray-800 p-4">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 w-9 h-9 rounded-lg bg-gray-700 flex items-center justify-center">
          <Clock size={18} className="text-gray-300" />
        </div>
        <div className="flex-1">
          <div className="text-white font-medium">Shift system</div>
          <div className="text-sm text-gray-400 mt-0.5">
            When off, bankers can process deposits and withdrawals without
            opening a shift. Per-PIN attribution still applies. Turn this on
            for cash-drawer reconciliation grouped by shift.
          </div>
          <div className="mt-3 flex items-center gap-x-6">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={enabled}
                disabled={saving}
                onChange={(e) => onChange({ enabled: e.target.checked })}
                className="w-4 h-4 accent-amber-500"
              />
              <span className="text-sm text-gray-200">
                Enable shift system
              </span>
            </label>
          </div>
        </div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------
// ReceiptPrintingPanel — admin settings for thermal receipts
// ----------------------------------------------------------------
// Admin-only. Controls whether a receipt auto-prints after a successful
// deposit/redemption and how many copies print (e.g. a guest copy + a
// cage file copy). A manual "Print receipt" button is always available
// on the receipt modal regardless of the auto-print setting.
export function ReceiptPrintingPanel({ settings, onChange, saving }) {
  const autoPrint = !!settings?.autoPrint;
  const copies = settings?.copies ?? 2;
  const savedHost = settings?.printerHost || '';

  // Local draft so typing doesn't fire a save on every keystroke — the
  // host is committed on blur / Enter.
  const [draftHost, setDraftHost] = useState(savedHost);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    setDraftHost(savedHost);
  }, [savedHost]);

  const commitHost = () => {
    const next = draftHost.trim();
    if (next !== savedHost) onChange({ printerHost: next });
  };

  const doTestPrint = async () => {
    const host = draftHost.trim();
    if (!host) {
      toast.error('Enter the printer IP / host first.');
      return;
    }
    // Persist the host first so a passing test reflects saved state.
    if (host !== savedHost) onChange({ printerHost: host });
    setTesting(true);
    const res = await eposPrintReceipt(
      {
        type: 'deposit',
        dateTime: new Date().toISOString(),
        reference: 'TEST0001',
        playerName: 'Test Print',
        memberNumber: '000000',
        amountCents: 10000,
        balanceAfterCents: 25000,
        cashierEmployeeNumber: null,
      },
      1,
      host,
      { label: 'TEST RECEIPT' }
    );
    setTesting(false);
    if (res.ok) toast.success('Test receipt sent to the printer.');
    else toast.error(res.error || 'Test print failed.');
  };

  return (
    <section className="rounded-xl border border-gray-700 bg-gray-800 p-4">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 w-9 h-9 rounded-lg bg-gray-700 flex items-center justify-center">
          <Printer size={18} className="text-gray-300" />
        </div>
        <div className="flex-1">
          <div className="text-white font-medium">Receipt printing</div>
          <div className="text-sm text-gray-400 mt-0.5">
            Prints an 80mm receipt to the Epson TM-T88VII after a deposit or
            withdrawal. Receipts are sent straight to the printer over the
            network — no print dialog. A Print receipt button is always
            available on the confirmation screen.
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-3">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={autoPrint}
                disabled={saving}
                onChange={(e) => onChange({ autoPrint: e.target.checked })}
                className="w-4 h-4 accent-amber-500"
              />
              <span className="text-sm text-gray-200">
                Auto-print on successful transaction
              </span>
            </label>
            <label className="flex items-center gap-2">
              <span className="text-sm text-gray-200">Copies</span>
              <select
                value={copies}
                disabled={saving}
                onChange={(e) => onChange({ copies: Number(e.target.value) })}
                className="bg-gray-900 border border-gray-700 rounded-lg text-white px-2 py-1 text-sm focus:outline-none focus:border-amber-500"
              >
                {[1, 2, 3, 4].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-200">Printer IP / host</span>
              <input
                type="text"
                value={draftHost}
                disabled={saving}
                placeholder="e.g. 192.168.1.50"
                onChange={(e) => setDraftHost(e.target.value)}
                onBlur={commitHost}
                onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                className="w-56 bg-gray-900 border border-gray-700 rounded-lg text-white px-3 py-1.5 text-sm focus:outline-none focus:border-amber-500"
              />
            </label>
            <button
              type="button"
              onClick={doTestPrint}
              disabled={saving || testing || !draftHost.trim()}
              className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-100 text-sm font-medium rounded-lg disabled:opacity-50 flex items-center gap-2"
            >
              {testing ? (
                <RefreshCw className="animate-spin" size={14} />
              ) : (
                <Printer size={14} />
              )}
              Test print
            </button>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Leave the printer IP blank to fall back to the browser print
            dialog. One-time setup: give the printer a static IP, then open
            its address in this browser once and accept its security
            certificate.
          </div>
        </div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------
// ReceiptModal — post-transaction receipt preview + print
// ----------------------------------------------------------------
// Shown after a successful deposit or redemption. Renders a faithful
// preview of the 80mm receipt and prints to the TM-T88VII.
//
// Printing prefers the ePOS-Print path (straight to the printer over
// the network, no dialog). If no printer IP is configured, or the ePOS
// request fails, it falls back to the browser print path so the
// operator always gets a receipt. Auto-prints on open when the admin
// setting is enabled; the Print button is always available for reprints.
function ReceiptModal({ receipt, copies, autoPrint, printerHost, onClose }) {
  const printedRef = useRef(false);
  const previewHtml = buildReceiptHTML(receipt, { copyTotal: copies });
  const [status, setStatus] = useState('idle'); // idle | printing | sent | browser
  const [message, setMessage] = useState('');

  const doPrint = useCallback(async () => {
    setStatus('printing');
    setMessage('');
    const host = (printerHost || '').trim();
    if (host) {
      const res = await eposPrintReceipt(receipt, copies, host);
      if (res.ok) {
        setStatus('sent');
        return;
      }
      // ePOS failed — fall back to the browser print path so the
      // operator still gets a receipt, and surface why.
      setMessage(
        (res.error || 'Direct printing failed.') +
          ' Used the browser print dialog instead.'
      );
      setStatus('browser');
      await printReceipt(receipt, copies);
      return;
    }
    // No printer IP configured — browser print path.
    setStatus('browser');
    await printReceipt(receipt, copies);
  }, [receipt, copies, printerHost]);

  useEffect(() => {
    if (autoPrint && !printedRef.current) {
      printedRef.current = true;
      const t = setTimeout(doPrint, 350);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [autoPrint, doPrint]);

  const baseTitle =
    receipt.type === 'deposit'
      ? 'Deposit'
      : receipt.type === 'credit_draw'
        ? 'Credit drawn'
        : receipt.type === 'credit_payment'
          ? 'Balance paid'
          : 'Withdrawal';
  const headerTitle = receipt.reprint ? `${baseTitle} — reprint` : `${baseTitle} complete`;
  const copyText = copies + ' ' + (copies === 1 ? 'copy' : 'copies');
  const printing = status === 'printing';

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-md p-6 space-y-4">
        <div className="flex flex-col items-center text-center gap-1">
          <div className="w-12 h-12 rounded-full bg-emerald-500/15 flex items-center justify-center">
            <CheckCircle size={26} className="text-emerald-400" />
          </div>
          <h3 className="text-lg font-semibold text-white">
            {headerTitle}
          </h3>
          <p className="text-sm text-gray-400">
            {fmtCents(receipt.amountCents)} · {receipt.playerName}
          </p>
        </div>

        <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 flex justify-center">
          <iframe
            title="Receipt preview"
            srcDoc={previewHtml}
            className="bg-white rounded"
            style={{ width: 288, height: 320, border: 'none' }}
            onLoad={(e) => {
              try {
                const h = e.target.contentWindow.document.body.scrollHeight;
                e.target.style.height =
                  String(Math.min(Math.max(h + 6, 200), 470)) + 'px';
              } catch (err) {
                /* preview height stays at its default */
              }
            }}
          />
        </div>

        <div className="text-center text-xs">
          {status === 'idle' && (
            <span className="text-gray-500">
              {printerHost
                ? 'Print will send ' + copyText + ' to ' + printerHost + '.'
                : 'No printer IP set — Print uses the browser dialog.'}
            </span>
          )}
          {printing && (
            <span className="text-gray-400">
              Sending {copyText} to the printer…
            </span>
          )}
          {status === 'sent' && (
            <span className="text-emerald-400">
              Sent {copyText} to the printer.
            </span>
          )}
          {status === 'browser' && (
            <span className="text-amber-400">
              {message || 'Printed ' + copyText + ' through the browser.'}
            </span>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-400 hover:text-white"
          >
            Done
          </button>
          <button
            onClick={doPrint}
            disabled={printing}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            <Printer size={16} />
            {status === 'idle' ? 'Print receipt' : 'Print again'}
          </button>
        </div>
      </div>
    </div>
  );
}
